// src/pages/FinancialReport.jsx
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { FaDollarSign } from 'react-icons/fa';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './FinancialReport.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function FinancialReport() {
  const { token } = useContext(AuthContext);
  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]); // Store all records
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [total,   setTotal]   = useState(0);
  const [summary, setSummary] = useState({
    totalRevenue: 0,
    totalReservations: 0,
    averageReservationValue: 0
  });
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        // Fetch approved grave reservations for financial report
        const res = await axios.get(
          `${API_BASE}/admin/grave-reservations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Filter only approved reservations for financial data
        const approvedReservations = res.data.reservations?.filter(r => r.status === 'approved') || [];
        const data = approvedReservations.map(reservation => ({
          _id: reservation._id,
          date: reservation.createdAt,
          description: `Grave ${reservation.graveId || `${reservation.garden}-${reservation.row}-${reservation.column}`} - ${reservation.clientName}`,
          amount: reservation.totalPrice || 0
        }));
        
        setAllRecords(data); // Store all records
        setRecords(data);

        // Calculate financial summary from grave reservations
        const totalRevenue = data.reduce((acc, rec) => acc + (parseFloat(rec.amount) || 0), 0);
        const totalReservations = data.length;
        const averageReservationValue = totalReservations > 0 ? totalRevenue / totalReservations : 0;

        setTotal(totalRevenue);
        setSummary({
          totalRevenue,
          totalReservations,
          averageReservationValue
        });

        setError(null);
      } catch (err) {
        console.error('Financial report fetch error:', err);
        setError('Failed to load financial report. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [API_BASE, token]);

  // Filter records based on date filter
  useEffect(() => {
    if (allRecords.length === 0) return;

    const now = new Date();
    let filtered = allRecords;

    if (dateFilter === 'week') {
      // Get date from one week ago
      const oneWeekAgo = new Date(now);
      oneWeekAgo.setDate(now.getDate() - 7);
      filtered = allRecords.filter(record => new Date(record.date) >= oneWeekAgo);
    } else if (dateFilter === 'month') {
      // Get date from one month ago
      const oneMonthAgo = new Date(now);
      oneMonthAgo.setMonth(now.getMonth() - 1);
      filtered = allRecords.filter(record => new Date(record.date) >= oneMonthAgo);
    } else if (dateFilter === 'year') {
      // Get date from one year ago
      const oneYearAgo = new Date(now);
      oneYearAgo.setFullYear(now.getFullYear() - 1);
      filtered = allRecords.filter(record => new Date(record.date) >= oneYearAgo);
    }

    setRecords(filtered);

    // Recalculate summary based on filtered data
    const totalRevenue = filtered.reduce((acc, rec) => acc + (parseFloat(rec.amount) || 0), 0);
    const totalReservations = filtered.length;
    const averageReservationValue = totalReservations > 0 ? totalRevenue / totalReservations : 0;

    setTotal(totalRevenue);
    setSummary({
      totalRevenue,
      totalReservations,
      averageReservationValue
    });

    // Reset to first page when filter changes
    setCurrentPage(1);
  }, [dateFilter, allRecords]);

  // Prepare chart data - Revenue over time (monthly)
  const prepareMonthlyData = () => {
    const monthlyData = {};
    
    records.forEach(record => {
      const date = new Date(record.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { revenue: 0, count: 0 };
      }
      monthlyData[monthKey].revenue += parseFloat(record.amount) || 0;
      monthlyData[monthKey].count += 1;
    });

    // Sort by date and get last 12 months
    const sortedKeys = Object.keys(monthlyData).sort().slice(-12);
    const labels = sortedKeys.map(key => {
      const [year, month] = key.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    });
    const revenueData = sortedKeys.map(key => monthlyData[key].revenue);
    const countData = sortedKeys.map(key => monthlyData[key].count);

    return { labels, revenueData, countData };
  };

  // Prepare period comparison data
  const preparePeriodComparison = () => {
    const now = new Date();
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(now.getMonth() - 1);
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(now.getFullYear() - 1);

    const weekRevenue = allRecords
      .filter(r => new Date(r.date) >= oneWeekAgo)
      .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    
    const monthRevenue = allRecords
      .filter(r => new Date(r.date) >= oneMonthAgo)
      .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    
    const yearRevenue = allRecords
      .filter(r => new Date(r.date) >= oneYearAgo)
      .reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

    return {
      labels: ['This Week', 'This Month', 'This Year'],
      data: [weekRevenue, monthRevenue, yearRevenue]
    };
  };

  const monthlyChartData = prepareMonthlyData();
  const periodComparisonData = preparePeriodComparison();

  // Line chart configuration
  const revenueLineChart = {
    labels: monthlyChartData.labels,
    datasets: [
      {
        label: 'Monthly Revenue',
        data: monthlyChartData.revenueData,
        borderColor: 'rgb(102, 126, 234)',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        fill: true,
        tension: 0.4,
      }
    ]
  };

  // Bar chart configuration
  const periodBarChart = {
    labels: periodComparisonData.labels,
    datasets: [
      {
        label: 'Revenue',
        data: periodComparisonData.data,
        backgroundColor: [
          'rgba(67, 233, 123, 0.8)',
          'rgba(79, 172, 254, 0.8)',
          'rgba(108, 92, 231, 0.8)'
        ],
        borderColor: [
          'rgb(67, 233, 123)',
          'rgb(79, 172, 254)',
          'rgb(108, 92, 231)'
        ],
        borderWidth: 2,
      }
    ]
  };

  // Doughnut chart - Reservations count by month
  const reservationsDoughnut = {
    labels: monthlyChartData.labels.slice(-6), // Last 6 months
    datasets: [
      {
        label: 'Reservations',
        data: monthlyChartData.countData.slice(-6),
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)'
        ],
        borderWidth: 2,
      }
    ]
  };

  // Pagination logic
  const totalPages = Math.ceil(records.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRecords = records.slice(startIndex, endIndex);

  if (loading) return <div className="loading-spinner">Loading financial report...</div>;
  if (error)   return <div className="error-message">{error}</div>;

  return (
    <div className="financial-report-page">
      <div className="financial-report-header-FIXED">
        <div className="header-content">
          <div className="header-icon">
            <FaDollarSign />
          </div>
          <div className="header-text">
            <h2>Financial Report</h2>
            <p>Approved Reservations</p>
          </div>
        </div>
      </div>

      {/* Date Filter Buttons */}
      <div className="date-filter-section">
        <label className="filter-label">Time Period:</label>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${dateFilter === 'all' ? 'active' : ''}`}
            onClick={() => setDateFilter('all')}
          >
            All Time
          </button>
          <button 
            className={`filter-btn ${dateFilter === 'week' ? 'active' : ''}`}
            onClick={() => setDateFilter('week')}
          >
            This Week
          </button>
          <button 
            className={`filter-btn ${dateFilter === 'month' ? 'active' : ''}`}
            onClick={() => setDateFilter('month')}
          >
            This Month
          </button>
          <button 
            className={`filter-btn ${dateFilter === 'year' ? 'active' : ''}`}
            onClick={() => setDateFilter('year')}
          >
            This Year
          </button>
        </div>
      </div>
      
      {/* Financial Summary Cards */}
      <div className="summary-stats">
        <div className="stat-box total">
          <span className="stat-label">Total Revenue</span>
          <span className="stat-value">
            ₱{summary.totalRevenue?.toLocaleString() || '0'}
          </span>
        </div>
        <div className="stat-box approved">
          <span className="stat-label">Total Reservations</span>
          <span className="stat-value">{summary.totalReservations || 0}</span>
        </div>
        <div className="stat-box average">
          <span className="stat-label">Average Reservation Value</span>
          <span className="stat-value">
            ₱{summary.averageReservationValue?.toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            }) || '0.00'}
          </span>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container-large">
          <h3 className="chart-title">Revenue Trend (Last 12 Months)</h3>
          <div className="chart-wrapper">
            <Line 
              data={revenueLineChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top'
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        return `Revenue: ₱${context.parsed.y.toLocaleString()}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return '₱' + value.toLocaleString();
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="charts-grid">
          <div className="chart-container">
            <h3 className="chart-title">Period Comparison</h3>
            <div className="chart-wrapper">
              <Bar 
                data={periodBarChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return `Revenue: ₱${context.parsed.y.toLocaleString()}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value) {
                          return '₱' + value.toLocaleString();
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          <div className="chart-container">
            <h3 className="chart-title">Reservations Distribution (Last 6 Months)</h3>
            <div className="chart-wrapper">
              <Doughnut 
                data={reservationsDoughnut}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: 'bottom'
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return `${context.label}: ${context.parsed} reservations`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Records Table */}
      <div className="table-section">
        <div className="section-header">
          <h3>Approved Reservation Details ({records.length} records)</h3>
          <div className="section-actions">
            <div className="page-size-control">
              <label>Show:</label>
              <select 
                value={pageSize} 
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="page-size-select"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>per page</span>
            </div>
          </div>
        </div>
        {records.length === 0 ? (
          <div className="no-records">
            <p>No approved reservations found.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map(({ _id, date, description, amount }) => (
                  <tr key={_id}>
                    <td>{new Date(date).toLocaleDateString()}</td>
                    <td>{description}</td>
                    <td className="amount-cell">
                      ₱{amount.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="btn btn-outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages} ({records.length} records)
              </span>
              <button 
                className="btn btn-outline"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Export/Print Button */}
      <div className="report-actions">
        <button 
          className="btn-export"
          onClick={() => window.print()}
        >
          Print Report
        </button>
      </div>
    </div>
  );
}
